import i18nFactory from '@yandex-int/i18n';
import { compact } from 'lodash';

import { AdvTypeTitles } from '../../service/BannerHelpers/BannerHelpers';
import { getTitleForCampaignType } from '../../service/CampaignHelpers/CampaignHelpers';
import { getEnumSetFrom } from '../../service/CastHelpers';
import {
    getDealStatusByStatusType,
    getDealTypeTitleByType
} from '../../service/DealsHelpers/DealsHelpers';
import {
    feedFiltersCriteriaSetTitlesDictionary,
    targetFunnelDictionary
} from '../../service/FeedFiltersHelpers/FeedFiltersHelpers';
import { autobudgetPriorityDictionary } from '../../service/PhrasesHelpers/PhrasesHelpers';
import { retargetingsCriteriaSetTitlesDictionary } from '../../service/RetargetingHelpers/RetargetingHelpers';
import { StrategyHelpers } from '../../service/StrategyHelpers/StrategyHelpers';
import {
    validateBooleanValue,
    validateDateValue,
    validateEnumValue,
    validatePositiveDoubleNumberValue,
    validatePositiveIntegerNumberValue,
    validatePositiveNumberSetValue,
    validateTextValue
} from '../../service/ValidationHelpers/ValidationHelpers';
import { banners, campaigns, ClientInfo, groups } from '../../typings/common';
import { dealStatusForFilter } from '../../typings/deals';
import {
    AdvType,
    BannerStatusForFilter,
    CampaignPlatform,
    CampaignStatusForFilter,
    CampaignType
} from '../../typings/domain';
import {
    CampaignFilterStrategyType,
    Criteria,
    CriteriaContainer,
    CriteriaRawContainer,
    CriteriaType,
    EnumSetItem,
    FilterExpressionValue
} from '../../typings/filter';
import { Goal } from '../../typings/goals';
import {
    DealType,
    GdAdGroupPrimaryStatus,
    GdAdGroupPrimaryStatusInput,
    GdAdPrimaryStatus,
    GdiRecommendationType,
    GdRetargetingBaseStatusInput,
    GdShowConditionAutobudgetPriority,
    GdShowConditionPrimaryStatusInput,
    GdSmartFilterBaseStatusInput,
    GdSmartFilterTargetFunnel
} from '../../typings/graphql-grid';
import { GdCampaignStrategyType } from '../../typings/graphql-log';
import { defaultClientInfo, GridViewType } from '../../typings/grid';

import * as keyset from './filter.i18n';

const i18n = i18nFactory(keyset);

export const StatisticsByAllGoals = 'StatisticsByAllGoals';

// @see https://wiki.yandex-team.ru/users/kkhl/NewDirect/Epics/campaignsGridFilter/
export enum CampaignCriteriaKey {
    // настройки
    id = 'id',
    types = 'types', // типы РК
    name = 'name',
    status = 'status',
    // hasDailyBudgetLimit = 'hasDailyBudgetLimit',
    startDate = 'startDate',
    endDate = 'endDate',
    platform = 'platform',
    recommendations = 'recommendations',
    strategy = 'strategy',
    hasBidModifiers = 'hasBidModifiers',
    hasBroadmatch = 'hasBroadmatch',
    hasMetrika = 'hasMetrika',
    hasSiteMonitoring = 'hasSiteMonitoring',

    // показатели
    cost = 'cost', // расход
    revenue = 'revenue', // доход
    profitability = 'profitability', // ROI
    avgGoalCost = 'avgGoalCost', // CPA
    avgClickCost = 'avgClickCost', //  CPC
    avgCpmCost = 'avgCpmCost', // CPM
    shows = 'shows',
    clicks = 'clicks',
    ctr = 'ctr',
    goals = 'goals', // конверсии
    conversionRate = 'conversionRate', // показатель конверсии
    bounceRate = 'bounceRate' // показатель отказов
}

export const FilterBooleanValueType = {
    ENABLED: i18n('enabled'),
    DISABLED: i18n('disabled')
};

const CampaignTypeEnumSetTitles: Record<CampaignType, string> = {
    [CampaignType.TEXT]: getTitleForCampaignType(CampaignType.TEXT),
    [CampaignType.MOBILE_CONTENT]: getTitleForCampaignType(
        CampaignType.MOBILE_CONTENT
    ),
    [CampaignType.PERFORMANCE]: getTitleForCampaignType(
        CampaignType.PERFORMANCE
    ),
    [CampaignType.DYNAMIC]: getTitleForCampaignType(CampaignType.DYNAMIC),
    [CampaignType.MCBANNER]: getTitleForCampaignType(CampaignType.MCBANNER),
    [CampaignType.CPM_BANNER]: getTitleForCampaignType(CampaignType.CPM_BANNER),
    [CampaignType.CPM_DEALS]: getTitleForCampaignType(CampaignType.CPM_DEALS),
    [CampaignType.CONTENT_PROMOTION]: getTitleForCampaignType(
        CampaignType.CONTENT_PROMOTION
    ),
    [CampaignType.CPM_YNDX_FRONTPAGE]: getTitleForCampaignType(
        CampaignType.CPM_YNDX_FRONTPAGE
    ),
    [CampaignType.INTERNAL_DISTRIB]: getTitleForCampaignType(
        CampaignType.INTERNAL_DISTRIB
    ),
    [CampaignType.INTERNAL_FREE]: getTitleForCampaignType(
        CampaignType.INTERNAL_FREE
    )
};

const PhraseStatusEnumSetTitles: Record<
    GdShowConditionPrimaryStatusInput,
    string
> = {
    ACTIVE: i18n('phrase-status-active'),
    DRAFT: i18n('phrase-status-draft'),
    ARCHIVED: i18n('phrase-status-archived'),
    STOPPED: i18n('phrase-status-suspended'),
    MODERATION_REJECTED: i18n('phrase-status-moderation-rejected')
};

// почему-то отличается от тех, что в методе getCampaignStatusTitleByType
const CampaignStatusEnumSetTitles: Record<CampaignStatusForFilter, string> = {
    ARCHIVED: i18n('archived'),
    STOPPED: i18n('stopped'),
    NO_MONEY: i18n('money-finished'),
    MODERATION_DENIED: i18n('moderation-denied'),
    TEMPORARILY_PAUSED: i18n('temporarily-paused'),
    ACTIVE: i18n('started'),
    DRAFT: i18n('draft'),
    MODERATION: i18n('moderation-pending')
};

const CampaignPlatformEnumSetTitles: { [key in CampaignPlatform]: string } = {
    SEARCH: i18n('search'),
    CONTEXT: i18n('context'),
    BOTH: i18n('all-platforms')
};

const GroupStatusEnumSetTitles: Record<GdAdGroupPrimaryStatusInput, string> = {
    ACTIVE: i18n('group-status-active'),
    ARCHIVED: i18n('group-status-archived'),
    DRAFT: i18n('group-status-draft'),
    MODERATION: i18n('group-status-moderation'),
    REJECTED: i18n('group-status-rejected'),
    STOPPED: i18n('group-status-stopped')
};

export const RecommendationEnumSetTitles: Record<
    GdiRecommendationType,
    string
> = {
    [GdiRecommendationType.dailyBudget]: i18n('daily-budget'),
    [GdiRecommendationType.removePagesFromBlackList]: i18n(
        'remove-page-from-blacklist'
    ),
    [GdiRecommendationType.removePagesFromBlackListOfACampaign]: i18n(
        'remove-page-from-blacklist'
    ),
    [GdiRecommendationType.changeAdGroupWithLowStat]: i18n(
        'change-adgroup-with-low-stat'
    ),
    [GdiRecommendationType.changeAdGroupForModeration]: i18n(
        'change-adgroup-for-moderation'
    ),
    [GdiRecommendationType.sendLicensesBanks]: i18n('send-licenses-banks'),
    [GdiRecommendationType.sendLicensesMedServices]: i18n(
        'send-licenses-med-services'
    ),
    [GdiRecommendationType.sendLicensesPharmacy]: i18n(
        'send-licenses-pharmacy'
    ),
    [GdiRecommendationType.switchOnAutotargeting]: i18n(
        'switch-on-autotargeting'
    ),
    [GdiRecommendationType.addMorePicturesToAdgroup]: i18n(
        'add-more-pictures-to-adgroup'
    ),
    [GdiRecommendationType.changeBannerForModeration]: i18n(
        'change-banner-for-moderation'
    ),
    [GdiRecommendationType.addBannerDisplayHrefs]: i18n(
        'add-banners-display-hrefs'
    ),
    [GdiRecommendationType.addTitleExtension]: i18n('add-title-extension'),
    [GdiRecommendationType.addSiteLinks]: i18n('add-site-links'),
    [GdiRecommendationType.addAdditionItemCallouts]: i18n(
        'add-additions-item-callouts'
    ),
    [GdiRecommendationType.addImageToBanner]: i18n('add-image-to-banner'),
    [GdiRecommendationType.addTurboWebSiteToBanner]: i18n(
        'add-turbo-web-site-to-banner'
    ),
    [GdiRecommendationType.addBannerFormats]: i18n('add-creatives'),
    [GdiRecommendationType.chooseAppropriatePlacementsForAdGroup]: i18n(
        'add-creatives'
    ),
    [GdiRecommendationType.chooseAppropriatePlacementsForBanner]: i18n(
        'add-placements'
    ),
    [GdiRecommendationType.uploadAppropriateCreatives]: i18n('add-creatives')
};

const isCriteriaAvailable = (
    criteria: Criteria | CriteriaContainer,
    options: ClientInfo
): boolean => {
    const criteriasAvailable = {
        revenue: options.hasEcommerce,
        profitability: options.hasEcommerce,
        avgGoalCost: options.hasCampaignsWithCPAStrategy,
        conversionRate: options.hasMetrikaCounters,
        bounceRate: options.hasMetrikaCounters
    };

    const hasPermission = criteriasAvailable.hasOwnProperty(criteria.key)
        ? criteriasAvailable[criteria.key]
        : true;

    if ('type' in criteria) {
        if (criteria.type === CriteriaType.enumSet) {
            return (
                hasPermission &&
                criteria.enumSet !== undefined &&
                criteria.enumSet.length !== 0
            );
        }
    }

    return hasPermission;
};

const isRecommendationTypeFilterAvailableForView = (
    type: GdiRecommendationType,
    view: GridViewType
): boolean => {
    const recommendationsViewGroups = {
        [campaigns]: [
            GdiRecommendationType.dailyBudget,
            GdiRecommendationType.removePagesFromBlackListOfACampaign
        ],
        [groups]: [
            GdiRecommendationType.changeAdGroupWithLowStat,
            GdiRecommendationType.changeAdGroupForModeration,
            GdiRecommendationType.switchOnAutotargeting
        ],
        [banners]: [
            GdiRecommendationType.changeBannerForModeration,
            GdiRecommendationType.addBannerDisplayHrefs,
            GdiRecommendationType.sendLicensesBanks,
            GdiRecommendationType.sendLicensesPharmacy,
            GdiRecommendationType.sendLicensesMedServices,
            GdiRecommendationType.addTitleExtension,
            GdiRecommendationType.addSiteLinks,
            GdiRecommendationType.addAdditionItemCallouts,
            GdiRecommendationType.addImageToBanner
        ]
    };

    return recommendationsViewGroups[view].includes(type);
};

const isRecommendationTypeAvailable = (
    type: GdiRecommendationType,
    view: GridViewType,
    options: ClientInfo
): boolean => {
    return (
        isRecommendationTypeFilterAvailableForView(type, view) &&
        (options.enabledRecommendationTypes === undefined ||
            options.enabledRecommendationTypes.includes(type))
    );
};

export const CampaignCriteriaSetTitles: Record<CampaignCriteriaKey, string> = {
    [CampaignCriteriaKey.id]: i18n('number'),
    [CampaignCriteriaKey.types]: i18n('campaign-type'),
    [CampaignCriteriaKey.name]: i18n('name'),
    [CampaignCriteriaKey.status]: i18n('status'),
    [CampaignCriteriaKey.startDate]: i18n('start-date'),
    [CampaignCriteriaKey.endDate]: i18n('finish-date'),
    [CampaignCriteriaKey.platform]: i18n('platform'),
    [CampaignCriteriaKey.strategy]: i18n('strategy'),
    [CampaignCriteriaKey.recommendations]: i18n('recommendations'),
    [CampaignCriteriaKey.hasBidModifiers]: i18n('bid-modifiers'),
    [CampaignCriteriaKey.hasBroadmatch]: i18n('broadmatch'),
    [CampaignCriteriaKey.hasMetrika]: i18n('metrika'),
    [CampaignCriteriaKey.hasSiteMonitoring]: i18n('site-monitoring'),
    [CampaignCriteriaKey.cost]: i18n('cost'),
    [CampaignCriteriaKey.revenue]: i18n('revenue'),
    [CampaignCriteriaKey.profitability]: i18n('profitability'),
    [CampaignCriteriaKey.avgGoalCost]: i18n('avgGoalCost'),
    [CampaignCriteriaKey.avgClickCost]: i18n('avgClickCost'),
    [CampaignCriteriaKey.avgCpmCost]: i18n('avgCpmCost'),
    [CampaignCriteriaKey.shows]: i18n('shows'),
    [CampaignCriteriaKey.clicks]: i18n('clicks'),
    [CampaignCriteriaKey.ctr]: i18n('ctr'),
    [CampaignCriteriaKey.goals]: i18n('goals'),
    [CampaignCriteriaKey.conversionRate]: i18n('converison-rate'),
    [CampaignCriteriaKey.bounceRate]: i18n('bounce-rate')
};

const getCampaignCriteria = (
    options: ClientInfo,
    isBidCorrectionSearchEnabled?: boolean,
    goals?: Record<string, Goal>,
    isGoalsStatEnabled?: boolean
): Record<CampaignCriteriaKey, Criteria> => {
    const enumSetFieldsForGoals = isGoalsStatEnabled
        ? getEnumSetFieldsForGoals(goals)
        : undefined;

    return {
        id: {
            title: CampaignCriteriaSetTitles[CampaignCriteriaKey.id],
            key: CampaignCriteriaKey.id,
            type: CriteriaType.positiveNumberSet,
            validate: validatePositiveNumberSetValue
        },
        types: {
            title: CampaignCriteriaSetTitles[CampaignCriteriaKey.types],
            key: CampaignCriteriaKey.types,
            type: CriteriaType.enumSet,
            enumSet: getEnumSetFrom(
                CampaignType,
                CampaignTypeEnumSetTitles
                // dima117a@todo DIRECT-91937, belyanskii@todo DIRECT-87186
            ).filter(type => {
                if (options.isInternalAdsAllowed) {
                    return (
                        type.key === CampaignType.INTERNAL_DISTRIB ||
                        type.key === CampaignType.INTERNAL_FREE
                    );
                }

                return (
                    type.key !== CampaignType.CONTENT_PROMOTION &&
                    type.key !== CampaignType.INTERNAL_DISTRIB &&
                    type.key !== CampaignType.INTERNAL_FREE
                );
            }),
            validate: validateEnumValue(CampaignType)
        },
        name: {
            title: CampaignCriteriaSetTitles[CampaignCriteriaKey.name],
            key: CampaignCriteriaKey.name,
            type: CriteriaType.text,
            validate: validateTextValue
        },
        status: {
            title: CampaignCriteriaSetTitles[CampaignCriteriaKey.status],
            key: CampaignCriteriaKey.status,
            type: CriteriaType.enumSet,
            enumSet: getEnumSetFrom(
                CampaignStatusForFilter,
                CampaignStatusEnumSetTitles
            ),
            validate: validateEnumValue(CampaignStatusForFilter)
        },
        /* hasDailyBudgetLimit: {
            title: i18n('budgetLimit'),
            key: CampaignCriteriaKey.hasDailyBudgetLimit,
            type: CriteriaType.boolean,
            validate: validateBooleanValue,
            isAvailable: opt => opt.isWalletEnabled
        }, */
        startDate: {
            title: CampaignCriteriaSetTitles[CampaignCriteriaKey.startDate],
            key: CampaignCriteriaKey.startDate,
            type: CriteriaType.date,
            validate: validateDateValue
        },
        endDate: {
            title: CampaignCriteriaSetTitles[CampaignCriteriaKey.endDate],
            key: CampaignCriteriaKey.endDate,
            type: CriteriaType.date,
            validate: validateDateValue
        },
        platform: {
            title: CampaignCriteriaSetTitles[CampaignCriteriaKey.platform],
            key: CampaignCriteriaKey.platform,
            type: CriteriaType.enumSet,
            enumSet: getEnumSetFrom(
                CampaignPlatform,
                CampaignPlatformEnumSetTitles
            ),
            validate: validateEnumValue(CampaignPlatform)
        },
        strategy: {
            title: CampaignCriteriaSetTitles[CampaignCriteriaKey.strategy],
            key: CampaignCriteriaKey.strategy,
            type: CriteriaType.enumSet,
            enumSet: getEnumSetFrom(
                CampaignFilterStrategyType,
                StrategyHelpers.getCampaignStrategyNameEnumSetTitles(
                    isBidCorrectionSearchEnabled
                )
            ).filter(el => el.key !== CampaignFilterStrategyType.NO_PREMIUM), // TODO: оторвать NO_PREMIUM вместе с бэкэндом
            validate: validateEnumValue(GdCampaignStrategyType)
        },
        recommendations: {
            title:
                CampaignCriteriaSetTitles[CampaignCriteriaKey.recommendations],
            key: CampaignCriteriaKey.recommendations,
            type: CriteriaType.enumSet,
            enumSet: getEnumSetFrom(
                GdiRecommendationType,
                RecommendationEnumSetTitles
            ).filter(item =>
                isRecommendationTypeAvailable(
                    item.key as GdiRecommendationType,
                    campaigns,
                    options
                )
            ),
            validate: (value: FilterExpressionValue): boolean => {
                return (value.value as EnumSetItem[]).every(item =>
                    isRecommendationTypeAvailable(
                        item.key as GdiRecommendationType,
                        campaigns,
                        options
                    )
                );
            }
        },
        hasBidModifiers: {
            title:
                CampaignCriteriaSetTitles[CampaignCriteriaKey.hasBidModifiers],
            key: CampaignCriteriaKey.hasBidModifiers,
            type: CriteriaType.boolean,
            validate: validateBooleanValue
        },
        hasBroadmatch: {
            title: CampaignCriteriaSetTitles[CampaignCriteriaKey.hasBroadmatch],
            key: CampaignCriteriaKey.hasBroadmatch,
            type: CriteriaType.boolean,
            validate: validateBooleanValue
        },
        hasMetrika: {
            title: CampaignCriteriaSetTitles[CampaignCriteriaKey.hasMetrika],
            key: CampaignCriteriaKey.hasMetrika,
            type: CriteriaType.boolean,
            validate: validateBooleanValue
        },
        hasSiteMonitoring: {
            title:
                CampaignCriteriaSetTitles[
                    CampaignCriteriaKey.hasSiteMonitoring
                ],
            key: CampaignCriteriaKey.hasSiteMonitoring,
            type: CriteriaType.boolean,
            validate: validateBooleanValue
        },
        cost: {
            title: CampaignCriteriaSetTitles[CampaignCriteriaKey.cost],
            key: CampaignCriteriaKey.cost,
            type: CriteriaType.positiveNumber,
            validate: validatePositiveDoubleNumberValue
        },
        revenue: {
            title: CampaignCriteriaSetTitles[CampaignCriteriaKey.revenue],
            key: CampaignCriteriaKey.revenue,
            type: CriteriaType.positiveNumber,
            validate: validatePositiveDoubleNumberValue
        },
        profitability: {
            title: CampaignCriteriaSetTitles[CampaignCriteriaKey.profitability],
            key: CampaignCriteriaKey.profitability,
            type: CriteriaType.positiveNumber,
            validate: validatePositiveDoubleNumberValue
        },
        avgGoalCost: {
            title: CampaignCriteriaSetTitles[CampaignCriteriaKey.avgGoalCost],
            key: CampaignCriteriaKey.avgGoalCost,
            type: CriteriaType.positiveNumber,
            validate: validatePositiveDoubleNumberValue,
            enumSetFields: enumSetFieldsForGoals
        },
        avgClickCost: {
            title: CampaignCriteriaSetTitles[CampaignCriteriaKey.avgClickCost],
            key: CampaignCriteriaKey.avgClickCost,
            type: CriteriaType.positiveNumber,
            validate: validatePositiveDoubleNumberValue
        },
        avgCpmCost: {
            title: CampaignCriteriaSetTitles[CampaignCriteriaKey.avgCpmCost],
            key: CampaignCriteriaKey.avgCpmCost,
            type: CriteriaType.positiveNumber,
            validate: validatePositiveDoubleNumberValue
        },
        shows: {
            title: CampaignCriteriaSetTitles[CampaignCriteriaKey.shows],
            key: CampaignCriteriaKey.shows,
            type: CriteriaType.positiveNumber,
            validate: validatePositiveIntegerNumberValue
        },
        clicks: {
            title: CampaignCriteriaSetTitles[CampaignCriteriaKey.clicks],
            key: CampaignCriteriaKey.clicks,
            type: CriteriaType.positiveNumber,
            validate: validatePositiveIntegerNumberValue
        },
        ctr: {
            title: CampaignCriteriaSetTitles[CampaignCriteriaKey.ctr],
            key: CampaignCriteriaKey.ctr,
            type: CriteriaType.positiveNumber,
            validate: validatePositiveDoubleNumberValue
        },
        goals: {
            title: CampaignCriteriaSetTitles[CampaignCriteriaKey.goals],
            key: CampaignCriteriaKey.goals,
            type: CriteriaType.positiveNumber,
            validate: validatePositiveIntegerNumberValue,
            enumSetFields: enumSetFieldsForGoals
        },
        conversionRate: {
            title:
                CampaignCriteriaSetTitles[CampaignCriteriaKey.conversionRate],
            key: CampaignCriteriaKey.conversionRate,
            type: CriteriaType.positiveNumber,
            validate: validatePositiveDoubleNumberValue,
            enumSetFields: enumSetFieldsForGoals
        },
        bounceRate: {
            title: CampaignCriteriaSetTitles[CampaignCriteriaKey.bounceRate],
            key: CampaignCriteriaKey.bounceRate,
            type: CriteriaType.positiveNumber,
            validate: validatePositiveDoubleNumberValue
        }
    };
};

// compact для того, чтобы не показывать в фильтрах некоторые статы. Если нет фичи или условий - не попадает в массив
const getCampaignCriteriaContainers = (
    options: ClientInfo,
    isBidCorrectionSearchEnabled?: boolean,
    hasOnlyCpmCampaigns?: boolean,
    goals?: Record<string, Goal>,
    isGoalsStatEnabled?: boolean
): CriteriaContainer[] => {
    return [
        {
            title: i18n('settings'),
            key: 'settings',
            criteria: [
                CampaignCriteriaKey.id,
                CampaignCriteriaKey.types,
                CampaignCriteriaKey.name,
                CampaignCriteriaKey.status,
                // CampaignCriteriaKey.hasDailyBudgetLimit,
                CampaignCriteriaKey.startDate,
                CampaignCriteriaKey.endDate,
                CampaignCriteriaKey.platform,
                CampaignCriteriaKey.recommendations,
                CampaignCriteriaKey.strategy,
                CampaignCriteriaKey.hasBidModifiers,
                CampaignCriteriaKey.hasBroadmatch,
                CampaignCriteriaKey.hasMetrika,
                CampaignCriteriaKey.hasSiteMonitoring
            ]
        },
        {
            title: i18n('metrics'),
            key: 'metrics',
            criteria: compact([
                CampaignCriteriaKey.cost,
                CampaignCriteriaKey.revenue,
                CampaignCriteriaKey.profitability,
                CampaignCriteriaKey.avgGoalCost,
                !hasOnlyCpmCampaigns && CampaignCriteriaKey.avgClickCost,
                CampaignCriteriaKey.shows,
                hasOnlyCpmCampaigns && CampaignCriteriaKey.avgCpmCost,
                CampaignCriteriaKey.clicks,
                CampaignCriteriaKey.ctr,
                CampaignCriteriaKey.goals,
                CampaignCriteriaKey.conversionRate,
                CampaignCriteriaKey.bounceRate
            ])
        }
    ].map(container => ({
        ...container,
        criteria: container.criteria.map(
            key =>
                getCampaignCriteria(
                    options,
                    isBidCorrectionSearchEnabled,
                    goals,
                    isGoalsStatEnabled
                )[key]
        )
    }));
};

export const getCampaignFilterCriteriaContainers = (
    options: ClientInfo = defaultClientInfo,
    isBidCorrectionSearchEnabled?: boolean,
    hasOnlyCpmCampaigns?: boolean,
    goals?: Record<string, Goal>,
    isGoalsStatEnabled?: boolean
): CriteriaContainer[] => {
    return getCampaignCriteriaContainers(
        options,
        isBidCorrectionSearchEnabled,
        hasOnlyCpmCampaigns,
        goals,
        isGoalsStatEnabled
    ).map(container => {
        return {
            ...container,
            criteria: container.criteria.filter(criteria =>
                isCriteriaAvailable(criteria, options)
            )
        };
    });
};

export const getCampaignFilterCriteria = (
    options: ClientInfo,
    isBidCorrectionSearchEnabled?: boolean
): Criteria[] => {
    const campaignCriteria = getCampaignCriteria(
        options,
        isBidCorrectionSearchEnabled
    );

    return Object.keys(campaignCriteria).map(key => campaignCriteria[key]);
};

export const getAvailableCampaignFilterCriteria = (
    options: ClientInfo = defaultClientInfo,
    isBidCorrectionSearchEnabled?: boolean
): Criteria[] => {
    return getCampaignFilterCriteria(
        options,
        isBidCorrectionSearchEnabled
    ).filter(criteria => isCriteriaAvailable(criteria, options));
};

export enum GroupCriteriaKey {
    // настройки
    id = 'id',
    // types = 'types', // типы РК
    name = 'name',
    status = 'status',

    // показатели
    cost = 'cost', // расход
    revenue = 'revenue', // доход
    recommendations = 'recommendations', // рекомендации
    profitability = 'profitability', // ROI
    avgGoalCost = 'avgGoalCost', // CPA
    avgClickCost = 'avgClickCost', //  CPC
    avgCpmCost = 'avgCpmCost', //  CPM
    shows = 'shows',
    clicks = 'clicks',
    ctr = 'ctr',
    goals = 'goals', // конверсии
    conversionRate = 'conversionRate', // показатель конверсии
    bounceRate = 'bounceRate', // показатель отказов,
    libraryMinusPhrasePacks = 'libraryMinusPhrasePacks', // наборы минус-фраз
    nameMinusPhrasePacks = 'nameMinusPhrasePacks' // наборы минус-фраз
}

export const GroupCriteriaSetTitles: Record<GroupCriteriaKey, string> = {
    [GroupCriteriaKey.id]: i18n('number'),
    [GroupCriteriaKey.name]: i18n('name'),
    [GroupCriteriaKey.status]: i18n('status'),
    [GroupCriteriaKey.recommendations]: i18n('recommendations'),
    [GroupCriteriaKey.cost]: i18n('cost'),
    [GroupCriteriaKey.revenue]: i18n('revenue'),
    [GroupCriteriaKey.profitability]: i18n('profitability'),
    [GroupCriteriaKey.avgGoalCost]: i18n('avgGoalCost'),
    [GroupCriteriaKey.avgClickCost]: i18n('avgClickCost'),
    [GroupCriteriaKey.avgCpmCost]: i18n('avgCpmCost'),
    [GroupCriteriaKey.shows]: i18n('shows'),
    [GroupCriteriaKey.clicks]: i18n('clicks'),
    [GroupCriteriaKey.ctr]: i18n('ctr'),
    [GroupCriteriaKey.goals]: i18n('goals'),
    [GroupCriteriaKey.conversionRate]: i18n('converison-rate'),
    [GroupCriteriaKey.bounceRate]: i18n('bounce-rate'),
    [GroupCriteriaKey.libraryMinusPhrasePacks]: i18n('libraryMinusPhrasePacks'),
    [GroupCriteriaKey.nameMinusPhrasePacks]: i18n('name-minus-phrase-packs')
};

export const getGroupCriteria = (
    options: ClientInfo,
    goals?: Record<string, Goal>,
    isGoalsStatEnabled?: boolean
): Record<GroupCriteriaKey, Criteria> => {
    const enumSetFieldsForGoals = isGoalsStatEnabled
        ? getEnumSetFieldsForGoals(goals)
        : undefined;

    return {
        id: {
            title: GroupCriteriaSetTitles[GroupCriteriaKey.id],
            key: GroupCriteriaKey.id,
            type: CriteriaType.positiveNumberSet,
            validate: validatePositiveNumberSetValue
        },
        name: {
            title: GroupCriteriaSetTitles[GroupCriteriaKey.name],
            key: GroupCriteriaKey.name,
            type: CriteriaType.text,
            validate: validateTextValue
        },
        status: {
            title: GroupCriteriaSetTitles[GroupCriteriaKey.status],
            key: GroupCriteriaKey.status,
            type: CriteriaType.enumSet,
            enumSet: getEnumSetFrom(
                GdAdGroupPrimaryStatusInput,
                GroupStatusEnumSetTitles
            ),
            validate: validateEnumValue(GdAdGroupPrimaryStatus)
        },
        recommendations: {
            title: GroupCriteriaSetTitles[GroupCriteriaKey.recommendations],
            key: GroupCriteriaKey.recommendations,
            type: CriteriaType.enumSet,
            enumSet: getEnumSetFrom(
                GdiRecommendationType,
                RecommendationEnumSetTitles
            ).filter(item =>
                isRecommendationTypeAvailable(
                    item.key as GdiRecommendationType,
                    groups,
                    options
                )
            ),
            validate: (value: FilterExpressionValue): boolean => {
                return (value.value as EnumSetItem[]).every(item =>
                    isRecommendationTypeAvailable(
                        item.key as GdiRecommendationType,
                        groups,
                        options
                    )
                );
            }
        },
        cost: {
            title: GroupCriteriaSetTitles[GroupCriteriaKey.cost],
            key: GroupCriteriaKey.cost,
            type: CriteriaType.positiveNumber,
            validate: validatePositiveDoubleNumberValue
        },
        revenue: {
            title: GroupCriteriaSetTitles[GroupCriteriaKey.revenue],
            key: GroupCriteriaKey.revenue,
            type: CriteriaType.positiveNumber,
            validate: validatePositiveDoubleNumberValue
        },
        profitability: {
            title: GroupCriteriaSetTitles[GroupCriteriaKey.profitability],
            key: GroupCriteriaKey.profitability,
            type: CriteriaType.positiveNumber,
            validate: validatePositiveDoubleNumberValue
        },
        avgGoalCost: {
            title: GroupCriteriaSetTitles[GroupCriteriaKey.avgGoalCost],
            key: GroupCriteriaKey.avgGoalCost,
            type: CriteriaType.positiveNumber,
            validate: validatePositiveDoubleNumberValue,
            enumSetFields: enumSetFieldsForGoals
        },
        avgClickCost: {
            title: GroupCriteriaSetTitles[GroupCriteriaKey.avgClickCost],
            key: GroupCriteriaKey.avgClickCost,
            type: CriteriaType.positiveNumber,
            validate: validatePositiveDoubleNumberValue
        },
        avgCpmCost: {
            title: GroupCriteriaSetTitles[GroupCriteriaKey.avgCpmCost],
            key: GroupCriteriaKey.avgCpmCost,
            type: CriteriaType.positiveNumber,
            validate: validatePositiveDoubleNumberValue
        },
        shows: {
            title: GroupCriteriaSetTitles[GroupCriteriaKey.shows],
            key: GroupCriteriaKey.shows,
            type: CriteriaType.positiveNumber,
            validate: validatePositiveIntegerNumberValue
        },
        clicks: {
            title: GroupCriteriaSetTitles[GroupCriteriaKey.clicks],
            key: GroupCriteriaKey.clicks,
            type: CriteriaType.positiveNumber,
            validate: validatePositiveIntegerNumberValue
        },
        ctr: {
            title: GroupCriteriaSetTitles[GroupCriteriaKey.ctr],
            key: GroupCriteriaKey.ctr,
            type: CriteriaType.positiveNumber,
            validate: validatePositiveDoubleNumberValue
        },
        goals: {
            title: GroupCriteriaSetTitles[GroupCriteriaKey.goals],
            key: GroupCriteriaKey.goals,
            type: CriteriaType.positiveNumber,
            validate: validatePositiveIntegerNumberValue,
            enumSetFields: enumSetFieldsForGoals
        },
        conversionRate: {
            title: GroupCriteriaSetTitles[GroupCriteriaKey.conversionRate],
            key: GroupCriteriaKey.conversionRate,
            type: CriteriaType.positiveNumber,
            validate: validatePositiveDoubleNumberValue,
            enumSetFields: enumSetFieldsForGoals
        },
        bounceRate: {
            title: GroupCriteriaSetTitles[GroupCriteriaKey.bounceRate],
            key: GroupCriteriaKey.bounceRate,
            type: CriteriaType.positiveNumber,
            validate: validatePositiveDoubleNumberValue
        },
        libraryMinusPhrasePacks: {
            title:
                GroupCriteriaSetTitles[
                    GroupCriteriaKey.libraryMinusPhrasePacks
                ],
            key: GroupCriteriaKey.libraryMinusPhrasePacks,
            type: CriteriaType.positiveNumberSet,
            validate: validatePositiveNumberSetValue
        },
        nameMinusPhrasePacks: {
            title:
                GroupCriteriaSetTitles[GroupCriteriaKey.nameMinusPhrasePacks],
            key: GroupCriteriaKey.nameMinusPhrasePacks,
            type: CriteriaType.text,
            validate: validateTextValue
        }
    };
};

// compact для того, чтобы не показывать в фильтрах некоторые статы. Если нет фичи или условий - не попадает в массив
const getGroupCriteriaContainers = (
    options: ClientInfo,
    isCpmCampaignsEnabled?: boolean,
    goals?: Record<string, Goal>,
    isGoalsStatEnabled?: boolean
): CriteriaContainer[] => {
    const data: CriteriaRawContainer[] = [
        {
            title: i18n('settings'),
            key: 'settings',
            criteria: [
                GroupCriteriaKey.id,
                GroupCriteriaKey.name,
                GroupCriteriaKey.recommendations,
                GroupCriteriaKey.status,
                {
                    title: i18n('minus-phrase-packs'),
                    key: 'minus-phrase-packs',
                    parent: 'settings',
                    criteria: [
                        GroupCriteriaKey.libraryMinusPhrasePacks,
                        GroupCriteriaKey.nameMinusPhrasePacks
                    ]
                }
            ]
        },
        {
            title: i18n('metrics'),
            key: 'metrics',
            criteria: compact([
                GroupCriteriaKey.cost,
                GroupCriteriaKey.revenue,
                GroupCriteriaKey.profitability,
                GroupCriteriaKey.avgGoalCost,
                !isCpmCampaignsEnabled && GroupCriteriaKey.avgClickCost,
                GroupCriteriaKey.shows,
                isCpmCampaignsEnabled && GroupCriteriaKey.avgCpmCost,
                GroupCriteriaKey.clicks,
                GroupCriteriaKey.ctr,
                GroupCriteriaKey.goals,
                GroupCriteriaKey.conversionRate,
                GroupCriteriaKey.bounceRate
            ])
        }
    ];
    return generateGroup(data, options, goals, isGoalsStatEnabled);
};

const generateGroupHelper = (
    data: CriteriaRawContainer,
    options: ClientInfo,
    goals?: Record<string, Goal>,
    isGoalsStatEnabled?: boolean
): CriteriaContainer => ({
    ...data,
    criteria: data.criteria.map(key =>
        typeof key !== 'string'
            ? generateGroupHelper(key, options, goals, isGoalsStatEnabled)
            : getGroupCriteria(options, goals, isGoalsStatEnabled)[key]
    )
});

const generateGroup = (
    data: CriteriaRawContainer[],
    options: ClientInfo,
    goals?: Record<string, Goal>,
    isGoalsStatEnabled?: boolean
): CriteriaContainer[] =>
    data.map(container =>
        generateGroupHelper(container, options, goals, isGoalsStatEnabled)
    );

export const getGroupFilterCriteriaContainers = (
    options: ClientInfo = defaultClientInfo,
    isCpmCampaignsEnabled?: boolean,
    goals?: Record<string, Goal>,
    isGoalsStatEnabled?: boolean
): CriteriaContainer[] => {
    return getGroupCriteriaContainers(
        options,
        isCpmCampaignsEnabled,
        goals,
        isGoalsStatEnabled
    ).map(container => {
        return {
            ...container,
            criteria: container.criteria.filter(criteria =>
                isCriteriaAvailable(criteria, options)
            )
        };
    });
};

export const getGroupFilterCriteria = (options: ClientInfo): Criteria[] => {
    const groupCriteria = getGroupCriteria(options);

    return Object.keys(groupCriteria).map(key => groupCriteria[key]);
};

export const getAvailableGroupFilterCriteria = (
    options: ClientInfo = defaultClientInfo
): Criteria[] => {
    return getGroupFilterCriteria(options).filter(criteria =>
        isCriteriaAvailable(criteria, options)
    );
};

export enum PhraseCriteriaKey {
    // настройки
    id = 'id',
    // types = 'types', // типы РК
    // name = 'name',
    keyword = 'keyword',

    // нужно ли это включать? у фильтра 2 значения ACTIVE и SUSPENDED GdiShowConditionBaseStatusInput
    status = 'status',
    autobudgetPriority = 'autobudgetPriority',

    // специфичные для фраз показатели
    price = 'price',
    priceContext = 'priceContext',

    // показатели
    cost = 'cost', // расход
    revenue = 'revenue', // доход
    profitability = 'profitability', // ROI
    avgGoalCost = 'avgGoalCost', // CPA
    avgClickCost = 'avgClickCost', //  CPC
    avgCpmCost = 'avgCpmCost', // CPM
    shows = 'shows',
    clicks = 'clicks',
    ctr = 'ctr',
    goals = 'goals', // конверсии
    conversionRate = 'conversionRate', // показатель конверсии
    bounceRate = 'bounceRate' // показатель отказов
}

export enum FeedFilterCriteriaKey {
    id = 'id',
    name = 'name',
    status = 'status',
    autobudgetPriority = 'autobudgetPriority',
    targetFunnel = 'targetFunnel',
    priceCpc = 'priceCpc',
    priceCpa = 'priceCpa',

    cost = 'cost',
    avgClickCost = 'avgClickCost',
    goals = 'goals'
}

export enum RetargetingCriteriaKey {
    name = 'name',
    statusIn = 'statusIn',
    price = 'price',

    // показатели
    cost = 'cost', // расход
    revenue = 'revenue', // доход
    profitability = 'profitability', // ROI
    avgGoalCost = 'avgGoalCost', // CPA
    avgClickCost = 'avgClickCost', //  CPC
    avgCpmCost = 'avgCpmCost', //  CPM
    shows = 'shows',
    clicks = 'clicks',
    ctr = 'ctr',
    goals = 'goals', // конверсии
    conversionRate = 'conversionRate', // показатель конверсии
    bounceRate = 'bounceRate' // показатель отказов
}

export enum UserProfileCriteriaKey {
    name = 'name',
    price = 'price',

    // показатели
    cost = 'cost', // расход
    shows = 'shows',
    avgCpmCost = 'avgCpmCost', //  CPM
    clicks = 'clicks',
    ctr = 'ctr',
    goals = 'goals', // конверсии
    conversionRate = 'conversionRate', // показатель конверсии
    bounceRate = 'bounceRate' // показатель отказов
}

export const phraseIdFieldName = i18n('number');

const getPhraseCriteria = (
    hasOnlyCpmCampaigns?: boolean,
    goals?: Record<string, Goal>,
    isGoalsStatEnabled?: boolean
): { [key in PhraseCriteriaKey]: Criteria } => {
    const enumSetFieldsForGoals = isGoalsStatEnabled
        ? getEnumSetFieldsForGoals(goals)
        : undefined;

    return {
        id: {
            title: phraseIdFieldName,
            key: PhraseCriteriaKey.id,
            type: CriteriaType.positiveNumberSet,
            validate: validatePositiveNumberSetValue
        },
        keyword: {
            title: i18n('keyword'),
            key: PhraseCriteriaKey.keyword,
            type: CriteriaType.text,
            validate: validateTextValue
        },
        status: {
            title: i18n('status'),
            key: PhraseCriteriaKey.status,
            type: CriteriaType.enumSet,
            enumSet: getEnumSetFrom(
                GdShowConditionPrimaryStatusInput,
                PhraseStatusEnumSetTitles
            ),
            validate: validateEnumValue(GdShowConditionPrimaryStatusInput)
        },
        autobudgetPriority: {
            title: i18n('autobudget-priority'),
            key: PhraseCriteriaKey.autobudgetPriority,
            type: CriteriaType.enumSet,
            enumSet: getEnumSetFrom(
                GdShowConditionAutobudgetPriority,
                autobudgetPriorityDictionary
            ),
            validate: validateEnumValue(GdShowConditionAutobudgetPriority)
        },
        price: {
            title: i18n('price'),
            key: PhraseCriteriaKey.price,
            type: CriteriaType.positiveNumber,
            validate: validatePositiveDoubleNumberValue
        },
        priceContext: {
            title: hasOnlyCpmCampaigns
                ? i18n('price-context-cpm')
                : i18n('price-context'),
            key: PhraseCriteriaKey.priceContext,
            type: CriteriaType.positiveNumber,
            validate: validatePositiveDoubleNumberValue
        },
        cost: {
            title: i18n('cost'),
            key: PhraseCriteriaKey.cost,
            type: CriteriaType.positiveNumber,
            validate: validatePositiveDoubleNumberValue
        },
        revenue: {
            title: i18n('revenue'),
            key: PhraseCriteriaKey.revenue,
            type: CriteriaType.positiveNumber,
            validate: validatePositiveDoubleNumberValue
        },
        profitability: {
            title: i18n('profitability'),
            key: PhraseCriteriaKey.profitability,
            type: CriteriaType.positiveNumber,
            validate: validatePositiveDoubleNumberValue
        },
        avgGoalCost: {
            title: i18n('avgGoalCost'),
            key: PhraseCriteriaKey.avgGoalCost,
            type: CriteriaType.positiveNumber,
            validate: validatePositiveDoubleNumberValue,
            enumSetFields: enumSetFieldsForGoals
        },
        avgClickCost: {
            title: i18n('avgClickCost'),
            key: PhraseCriteriaKey.avgClickCost,
            type: CriteriaType.positiveNumber,
            validate: validatePositiveDoubleNumberValue
        },
        avgCpmCost: {
            title: i18n('avgCpmCost'),
            key: PhraseCriteriaKey.avgCpmCost,
            type: CriteriaType.positiveNumber,
            validate: validatePositiveDoubleNumberValue
        },
        shows: {
            title: i18n('shows'),
            key: PhraseCriteriaKey.shows,
            type: CriteriaType.positiveNumber,
            validate: validatePositiveIntegerNumberValue
        },
        clicks: {
            title: i18n('clicks'),
            key: PhraseCriteriaKey.clicks,
            type: CriteriaType.positiveNumber,
            validate: validatePositiveIntegerNumberValue
        },
        ctr: {
            title: i18n('ctr'),
            key: PhraseCriteriaKey.ctr,
            type: CriteriaType.positiveNumber,
            validate: validatePositiveDoubleNumberValue
        },
        goals: {
            title: i18n('goals'),
            key: PhraseCriteriaKey.goals,
            type: CriteriaType.positiveNumber,
            validate: validatePositiveIntegerNumberValue,
            enumSetFields: enumSetFieldsForGoals
        },
        conversionRate: {
            title: i18n('converison-rate'),
            key: PhraseCriteriaKey.conversionRate,
            type: CriteriaType.positiveNumber,
            validate: validatePositiveDoubleNumberValue,
            enumSetFields: enumSetFieldsForGoals
        },
        bounceRate: {
            title: i18n('bounce-rate'),
            key: PhraseCriteriaKey.bounceRate,
            type: CriteriaType.positiveNumber,
            validate: validatePositiveDoubleNumberValue
        }
    };
};

const getFeedFilterCriteria = (
    goals?: Record<string, Goal>,
    isGoalsStatEnabled?: boolean
): { [key in FeedFilterCriteriaKey]: Criteria } => {
    const enumSetFieldsForGoals = isGoalsStatEnabled
        ? getEnumSetFieldsForGoals(goals)
        : undefined;

    return {
        id: {
            title: i18n('number'),
            key: FeedFilterCriteriaKey.id,
            type: CriteriaType.positiveNumberSet,
            validate: validatePositiveNumberSetValue
        },
        name: {
            title: i18n('name'),
            key: FeedFilterCriteriaKey.name,
            type: CriteriaType.text,
            validate: validateTextValue
        },
        status: {
            title: i18n('status'),
            key: FeedFilterCriteriaKey.status,
            type: CriteriaType.enumSet,
            enumSet: getEnumSetFrom(
                GdSmartFilterBaseStatusInput,
                feedFiltersCriteriaSetTitlesDictionary
            ),
            validate: validateEnumValue(GdSmartFilterBaseStatusInput)
        },
        targetFunnel: {
            title: i18n('target-funnel'),
            key: FeedFilterCriteriaKey.targetFunnel,
            type: CriteriaType.enumSet,
            enumSet: getEnumSetFrom(
                GdSmartFilterTargetFunnel,
                targetFunnelDictionary
            ),
            validate: validateEnumValue(GdSmartFilterTargetFunnel)
        },
        autobudgetPriority: {
            title: i18n('autobudget-priority'),
            key: FeedFilterCriteriaKey.autobudgetPriority,
            type: CriteriaType.enumSet,
            enumSet: getEnumSetFrom(
                GdShowConditionAutobudgetPriority,
                autobudgetPriorityDictionary
            ),
            validate: validateEnumValue(GdShowConditionAutobudgetPriority)
        },
        priceCpc: {
            title: i18n('cpc'),
            key: FeedFilterCriteriaKey.priceCpc,
            type: CriteriaType.positiveNumber,
            validate: validatePositiveDoubleNumberValue
        },
        priceCpa: {
            title: i18n('cpa'),
            key: FeedFilterCriteriaKey.priceCpa,
            type: CriteriaType.positiveNumber,
            validate: validatePositiveDoubleNumberValue
        },
        cost: {
            title: i18n('cost'),
            key: FeedFilterCriteriaKey.cost,
            type: CriteriaType.positiveNumber,
            validate: validatePositiveDoubleNumberValue
        },
        avgClickCost: {
            title: i18n('avgClickCost'),
            key: FeedFilterCriteriaKey.avgClickCost,
            type: CriteriaType.positiveNumber,
            validate: validatePositiveDoubleNumberValue
        },
        goals: {
            title: i18n('goals'),
            key: FeedFilterCriteriaKey.goals,
            type: CriteriaType.positiveNumber,
            validate: validatePositiveIntegerNumberValue,
            enumSetFields: enumSetFieldsForGoals
        }
    };
};

const getRetargetingCriteria = (): {
    [key in RetargetingCriteriaKey]: Criteria
} => {
    return {
        name: {
            title: i18n('name'),
            key: RetargetingCriteriaKey.name,
            type: CriteriaType.text,
            validate: validateTextValue
        },
        statusIn: {
            title: i18n('status'),
            key: RetargetingCriteriaKey.statusIn,
            type: CriteriaType.enumSet,
            enumSet: getEnumSetFrom(
                GdRetargetingBaseStatusInput,
                retargetingsCriteriaSetTitlesDictionary
            ),
            validate: validateEnumValue(GdRetargetingBaseStatusInput)
        },
        price: {
            title: i18n('context-price'),
            key: RetargetingCriteriaKey.price,
            type: CriteriaType.positiveNumber,
            validate: validatePositiveDoubleNumberValue
        },
        cost: {
            title: i18n('cost'),
            key: RetargetingCriteriaKey.cost,
            type: CriteriaType.positiveNumber,
            validate: validatePositiveDoubleNumberValue
        },
        revenue: {
            title: i18n('revenue'),
            key: RetargetingCriteriaKey.revenue,
            type: CriteriaType.positiveNumber,
            validate: validatePositiveDoubleNumberValue
        },
        profitability: {
            title: i18n('profitability'),
            key: RetargetingCriteriaKey.profitability,
            type: CriteriaType.positiveNumber,
            validate: validatePositiveDoubleNumberValue
        },
        avgGoalCost: {
            title: i18n('avgGoalCost'),
            key: RetargetingCriteriaKey.avgGoalCost,
            type: CriteriaType.positiveNumber,
            validate: validatePositiveDoubleNumberValue
        },
        avgClickCost: {
            title: i18n('avgClickCost'),
            key: RetargetingCriteriaKey.avgClickCost,
            type: CriteriaType.positiveNumber,
            validate: validatePositiveDoubleNumberValue
        },
        shows: {
            title: i18n('shows'),
            key: RetargetingCriteriaKey.shows,
            type: CriteriaType.positiveNumber,
            validate: validatePositiveIntegerNumberValue
        },
        avgCpmCost: {
            title: i18n('avgCpmCost'),
            key: RetargetingCriteriaKey.avgCpmCost,
            type: CriteriaType.positiveNumber,
            validate: validatePositiveDoubleNumberValue
        },
        clicks: {
            title: i18n('clicks'),
            key: RetargetingCriteriaKey.clicks,
            type: CriteriaType.positiveNumber,
            validate: validatePositiveIntegerNumberValue
        },
        ctr: {
            title: i18n('ctr'),
            key: RetargetingCriteriaKey.ctr,
            type: CriteriaType.positiveNumber,
            validate: validatePositiveDoubleNumberValue
        },
        goals: {
            title: i18n('goals'),
            key: RetargetingCriteriaKey.goals,
            type: CriteriaType.positiveNumber,
            validate: validatePositiveIntegerNumberValue
        },
        conversionRate: {
            title: i18n('converison-rate'),
            key: RetargetingCriteriaKey.conversionRate,
            type: CriteriaType.positiveNumber,
            validate: validatePositiveDoubleNumberValue
        },
        bounceRate: {
            title: i18n('bounce-rate'),
            key: RetargetingCriteriaKey.bounceRate,
            type: CriteriaType.positiveNumber,
            validate: validatePositiveDoubleNumberValue
        }
    };
};

const getUserProfileCriteria = (): {
    [key in UserProfileCriteriaKey]: Criteria
} => {
    return {
        name: {
            title: i18n('name'),
            key: UserProfileCriteriaKey.name,
            type: CriteriaType.text,
            validate: validateTextValue
        },
        price: {
            title: i18n('price-context-cpm'),
            key: UserProfileCriteriaKey.price,
            type: CriteriaType.positiveNumber,
            validate: validatePositiveDoubleNumberValue
        },
        cost: {
            title: i18n('cost'),
            key: UserProfileCriteriaKey.cost,
            type: CriteriaType.positiveNumber,
            validate: validatePositiveDoubleNumberValue
        },
        shows: {
            title: i18n('shows'),
            key: UserProfileCriteriaKey.shows,
            type: CriteriaType.positiveNumber,
            validate: validatePositiveIntegerNumberValue
        },
        avgCpmCost: {
            title: i18n('avgCpmCost'),
            key: UserProfileCriteriaKey.avgCpmCost,
            type: CriteriaType.positiveNumber,
            validate: validatePositiveDoubleNumberValue
        },
        clicks: {
            title: i18n('clicks'),
            key: UserProfileCriteriaKey.clicks,
            type: CriteriaType.positiveNumber,
            validate: validatePositiveIntegerNumberValue
        },
        ctr: {
            title: i18n('ctr'),
            key: UserProfileCriteriaKey.ctr,
            type: CriteriaType.positiveNumber,
            validate: validatePositiveDoubleNumberValue
        },
        goals: {
            title: i18n('goals'),
            key: UserProfileCriteriaKey.goals,
            type: CriteriaType.positiveNumber,
            validate: validatePositiveIntegerNumberValue
        },
        conversionRate: {
            title: i18n('converison-rate'),
            key: UserProfileCriteriaKey.conversionRate,
            type: CriteriaType.positiveNumber,
            validate: validatePositiveDoubleNumberValue
        },
        bounceRate: {
            title: i18n('bounce-rate'),
            key: UserProfileCriteriaKey.bounceRate,
            type: CriteriaType.positiveNumber,
            validate: validatePositiveDoubleNumberValue
        }
    };
};

// compact для того, чтобы не показывать в фильтрах некоторые статы. Если нет фичи или условий - не попадает в массив
const getPhraseCriteriaContainers = (
    hasOnlyCpmCampaigns?: boolean,
    goals?: Record<string, Goal>,
    isGoalsStatEnabled?: boolean
): CriteriaContainer[] => {
    return [
        {
            title: i18n('settings'),
            key: 'settings',
            criteria: [
                PhraseCriteriaKey.id,
                PhraseCriteriaKey.keyword,
                PhraseCriteriaKey.status,
                !hasOnlyCpmCampaigns && PhraseCriteriaKey.autobudgetPriority,
                PhraseCriteriaKey.price,
                PhraseCriteriaKey.priceContext
            ].filter(key => Boolean(key))
        },
        {
            title: i18n('metrics'),
            key: 'metrics',
            criteria: compact([
                PhraseCriteriaKey.cost,
                PhraseCriteriaKey.revenue,
                PhraseCriteriaKey.profitability,
                PhraseCriteriaKey.avgGoalCost,
                !hasOnlyCpmCampaigns && PhraseCriteriaKey.avgClickCost,
                PhraseCriteriaKey.shows,
                hasOnlyCpmCampaigns && PhraseCriteriaKey.avgCpmCost,
                PhraseCriteriaKey.clicks,
                PhraseCriteriaKey.ctr,
                PhraseCriteriaKey.goals,
                PhraseCriteriaKey.conversionRate,
                PhraseCriteriaKey.bounceRate
            ])
        }
    ].map(container => ({
        ...container,
        criteria: container.criteria.map(
            (key: PhraseCriteriaKey) =>
                getPhraseCriteria(
                    hasOnlyCpmCampaigns,
                    goals,
                    isGoalsStatEnabled
                )[key]
        )
    }));
};

export const getPhraseFilterCriteriaContainers = (
    options: ClientInfo = defaultClientInfo,
    hasOnlyCpmCampaigns?: boolean,
    goals?: Record<string, Goal>,
    isGoalsStatEnabled?: boolean
): CriteriaContainer[] => {
    return getPhraseCriteriaContainers(
        hasOnlyCpmCampaigns,
        goals,
        isGoalsStatEnabled
    ).map(container => {
        return {
            ...container,
            criteria: container.criteria.filter(criteria =>
                isCriteriaAvailable(criteria, options)
            )
        };
    });
};

const getFeedFilterCriteriaContainers = (
    goals?: Record<string, Goal>,
    isGoalsStatEnabled?: boolean
): CriteriaContainer[] => {
    return [
        {
            title: i18n('settings'),
            key: 'settings',
            criteria: [
                FeedFilterCriteriaKey.id,
                FeedFilterCriteriaKey.status,
                FeedFilterCriteriaKey.autobudgetPriority,
                FeedFilterCriteriaKey.targetFunnel,
                FeedFilterCriteriaKey.priceCpc,
                FeedFilterCriteriaKey.priceCpa
            ]
        },
        {
            title: i18n('metrics'),
            key: 'metrics',
            criteria: [
                FeedFilterCriteriaKey.cost,
                FeedFilterCriteriaKey.avgClickCost,
                FeedFilterCriteriaKey.goals
            ]
        }
    ].map(container => ({
        ...container,
        criteria: container.criteria.map(
            key => getFeedFilterCriteria(goals, isGoalsStatEnabled)[key]
        )
    }));
};

export const getFeedFilterFilterCriteriaContainers = (
    options: ClientInfo = defaultClientInfo,
    goals?: Record<string, Goal>,
    isGoalsStatEnabled?: boolean
): CriteriaContainer[] => {
    return getFeedFilterCriteriaContainers(goals, isGoalsStatEnabled).map(
        container => {
            return {
                ...container,
                criteria: container.criteria.filter(criteria =>
                    isCriteriaAvailable(criteria, options)
                )
            };
        }
    );
};

const getReretargeringCriteriaContainers = (): CriteriaContainer[] => {
    return [
        {
            title: i18n('settings'),
            key: 'settings',
            criteria: [
                RetargetingCriteriaKey.name,
                RetargetingCriteriaKey.statusIn,
                RetargetingCriteriaKey.price
            ]
        },
        {
            title: i18n('metrics'),
            key: 'metrics',
            criteria: [
                RetargetingCriteriaKey.cost,
                RetargetingCriteriaKey.revenue,
                RetargetingCriteriaKey.profitability,
                RetargetingCriteriaKey.avgGoalCost,
                RetargetingCriteriaKey.avgClickCost,
                RetargetingCriteriaKey.shows,
                RetargetingCriteriaKey.clicks,
                RetargetingCriteriaKey.ctr,
                RetargetingCriteriaKey.goals,
                RetargetingCriteriaKey.conversionRate,
                RetargetingCriteriaKey.bounceRate
            ]
        }
    ].map(container => ({
        ...container,
        criteria: container.criteria.map(key => getRetargetingCriteria()[key])
    }));
};

const getUserProfileCriteriaContainers = (): CriteriaContainer[] => {
    return [
        {
            title: i18n('settings'),
            key: 'settings',
            criteria: [
                UserProfileCriteriaKey.name,
                UserProfileCriteriaKey.price
            ]
        },
        {
            title: i18n('metrics'),
            key: 'metrics',
            criteria: [
                UserProfileCriteriaKey.cost,
                UserProfileCriteriaKey.shows,
                UserProfileCriteriaKey.avgCpmCost,
                UserProfileCriteriaKey.clicks,
                UserProfileCriteriaKey.ctr,
                UserProfileCriteriaKey.goals,
                UserProfileCriteriaKey.conversionRate,
                UserProfileCriteriaKey.bounceRate
            ]
        }
    ].map(container => ({
        ...container,
        criteria: container.criteria.map(key => getUserProfileCriteria()[key])
    }));
};

export const getTargetingFilterCriteriaContainers = (
    options: ClientInfo = defaultClientInfo
): CriteriaContainer[] => {
    return getReretargeringCriteriaContainers().map(container => {
        return {
            ...container,
            criteria: container.criteria.filter(criteria =>
                isCriteriaAvailable(criteria, options)
            )
        };
    });
};

export const getUserProfileFilterCriteriaContainers = (
    options: ClientInfo = defaultClientInfo
): CriteriaContainer[] => {
    return getUserProfileCriteriaContainers().map(container => {
        return {
            ...container,
            criteria: container.criteria.filter(criteria =>
                isCriteriaAvailable(criteria, options)
            )
        };
    });
};

export const getPhraseFilterCriteria = (
    hasOnlyCpmCampaigns?: boolean
): Criteria[] => {
    const phraseCriteria = getPhraseCriteria(hasOnlyCpmCampaigns);
    return Object.keys(phraseCriteria).map(key => phraseCriteria[key]);
};

export const getFeedFilterFilterCriteria = (): Criteria[] => {
    const feedFilterCriteria = getFeedFilterCriteria();
    return Object.keys(feedFilterCriteria).map(key => feedFilterCriteria[key]);
};

export const getTargetingFilterCriteria = (): Criteria[] => {
    const targetingCriteria = getRetargetingCriteria();
    return Object.keys(targetingCriteria).map(key => targetingCriteria[key]);
};

export const getUserProfileFilterCriteria = (): Criteria[] => {
    const userProfileCriteria = getUserProfileCriteria();

    return Object.keys(userProfileCriteria).map(
        key => userProfileCriteria[key]
    );
};

export const getAvailablePhraseFilterCriteria = (
    options: ClientInfo = defaultClientInfo,
    hasOnlyCpmCampaigns?: boolean
): Criteria[] => {
    return getPhraseFilterCriteria(hasOnlyCpmCampaigns).filter(criteria =>
        isCriteriaAvailable(criteria, options)
    );
};

export const getAvailableFeedFilterFilterCriteria = (
    options: ClientInfo = defaultClientInfo
): Criteria[] => {
    return getFeedFilterFilterCriteria().filter(criteria =>
        isCriteriaAvailable(criteria, options)
    );
};

export const getAvailableTargetingFilterCriteria = (
    options: ClientInfo = defaultClientInfo
): Criteria[] => {
    return getTargetingFilterCriteria().filter(criteria =>
        isCriteriaAvailable(criteria, options)
    );
};

export const getAvailableUsersProfileFilterCriteria = (
    options: ClientInfo = defaultClientInfo
): Criteria[] => {
    return getUserProfileFilterCriteria().filter(criteria =>
        isCriteriaAvailable(criteria, options)
    );
};

export enum BannerCriteriaKey {
    // настройки
    id = 'id', // bannerIdIncluded bannerIdExcluded
    types = 'types', // typeIn
    title = 'title', // titleContains titleNotContains
    titleExtension = 'titleExtension', // titleExtensionContains titleExtensionNotContains
    body = 'body', // bodyContains bodyNotContains
    recommendations = 'recommendations',
    hasImage = 'hasImage', // imageExists
    hasSitelinks = 'hasSitelinks', // sitelinksExists
    primaryStatus = 'primaryStatus',
    hasTurbolanding = 'hasTurbolanding', // turbolandingsExist
    // callouts = 'callouts' calloutsExists
    // vcard = 'vcard' //vcardExists
    // href = 'href' hrefContains

    // показатели
    cost = 'cost', // расход
    revenue = 'revenue', // доход
    profitability = 'profitability', // ROI
    avgGoalCost = 'avgGoalCost', // CPA
    avgClickCost = 'avgClickCost', //  CPC
    avgCpmCost = 'avgCpmCost', // CPM
    shows = 'shows',
    clicks = 'clicks',
    ctr = 'ctr',
    goals = 'goals', // конверсии
    conversionRate = 'conversionRate', // показатель конверсии
    bounceRate = 'bounceRate' // показатель отказов
}

const BannerStatusEnumSetTitles: Record<GdAdPrimaryStatus, string> = {
    [GdAdPrimaryStatus.ARCHIVED]: i18n('banner-status-archived'),
    [GdAdPrimaryStatus.MANUALLY_SUSPENDED]: i18n(
        'banner-status-manually-suspended'
    ),
    [GdAdPrimaryStatus.ACTIVE]: i18n('banner-status-active'),
    [GdAdPrimaryStatus.DRAFT]: i18n('banner-status-draft'),
    [GdAdPrimaryStatus.TEMPORARILY_SUSPENDED]: i18n(
        'banner-status-temporarly-suspended'
    ),
    [GdAdPrimaryStatus.MODERATION_REJECTED]: i18n(
        'banner-status-moderation-rejected'
    ),
    [GdAdPrimaryStatus.MODERATION]: i18n('banner-status-moderation')
};

export const BannerCriteriaSetTitles: Record<BannerCriteriaKey, string> = {
    [BannerCriteriaKey.id]: i18n('number'),
    [BannerCriteriaKey.types]: i18n('banner-type'),
    [BannerCriteriaKey.title]: i18n('banner-title'),
    [BannerCriteriaKey.titleExtension]: i18n('banner-title-extension'),
    [BannerCriteriaKey.body]: i18n('banner-body'),
    [BannerCriteriaKey.hasImage]: i18n('has-image'),
    [BannerCriteriaKey.hasSitelinks]: i18n('has-sitelinks'),
    [BannerCriteriaKey.primaryStatus]: i18n('status'),
    [BannerCriteriaKey.recommendations]: i18n('recommendations'),
    [BannerCriteriaKey.cost]: i18n('cost'),
    [BannerCriteriaKey.revenue]: i18n('revenue'),
    [BannerCriteriaKey.profitability]: i18n('profitability'),
    [BannerCriteriaKey.avgGoalCost]: i18n('avgGoalCost'),
    [BannerCriteriaKey.avgClickCost]: i18n('avgClickCost'),
    [BannerCriteriaKey.shows]: i18n('shows'),
    [BannerCriteriaKey.avgCpmCost]: i18n('avgCpmCost'),
    [BannerCriteriaKey.clicks]: i18n('clicks'),
    [BannerCriteriaKey.ctr]: i18n('ctr'),
    [BannerCriteriaKey.goals]: i18n('goals'),
    [BannerCriteriaKey.conversionRate]: i18n('converison-rate'),
    [BannerCriteriaKey.bounceRate]: i18n('bounce-rate'),
    [BannerCriteriaKey.hasTurbolanding]: i18n('has-turbolanding')
};

const getAdvTypeTitles = (): EnumSetItem[] => {
    const typeTitles = getEnumSetFrom(AdvType, AdvTypeTitles);

    // Название содержащее точку - "Баннер в Яндекс.Метро" нельзя использовать в фильтре т.к. символ точки является разделителем.
    // В этом месте происходит подмена названия "Баннер в Яндекс.Метро" на "Баннер в Яндекс Метро" (без точки)
    const indexMetroType = typeTitles.findIndex(
        el => el.key === AdvType.CPM_GEOPRODUCT
    );

    if (indexMetroType !== -1) {
        typeTitles[indexMetroType] = {
            ...typeTitles[indexMetroType],
            title: i18n('banner-type-cpm_metro')
        };
    }

    return typeTitles;
};

const getBannerCriteria = (
    options: ClientInfo,
    filtrateAdvTypes?: boolean,
    goals?: Record<string, Goal>,
    isGoalsStatEnabled?: boolean
): Record<BannerCriteriaKey, Criteria> => {
    const enumSetFieldsForGoals = isGoalsStatEnabled
        ? getEnumSetFieldsForGoals(goals)
        : undefined;

    let advTypeTitles: EnumSetItem[] = getAdvTypeTitles();

    if (filtrateAdvTypes) {
        const { isInternalAdsAllowed } = options;

        if (isInternalAdsAllowed) {
            advTypeTitles = advTypeTitles.filter(
                el => el.key === AdvType.INTERNAL
            );
        } else {
            advTypeTitles = advTypeTitles.filter(
                el => el.key !== AdvType.INTERNAL
            );
        }
    }

    return {
        id: {
            title: BannerCriteriaSetTitles[BannerCriteriaKey.id],
            key: BannerCriteriaKey.id,
            type: CriteriaType.positiveNumberSet,
            validate: validatePositiveNumberSetValue
        },

        types: {
            title: BannerCriteriaSetTitles[BannerCriteriaKey.types],
            key: BannerCriteriaKey.types,
            type: CriteriaType.enumSet,
            enumSet: advTypeTitles,
            validate: validateEnumValue(AdvType)
        },

        title: {
            title: BannerCriteriaSetTitles[BannerCriteriaKey.title],
            key: BannerCriteriaKey.title,
            type: CriteriaType.text,
            validate: validateTextValue
        },

        titleExtension: {
            title: BannerCriteriaSetTitles[BannerCriteriaKey.titleExtension],
            key: BannerCriteriaKey.titleExtension,
            type: CriteriaType.text,
            validate: validateTextValue
        },

        body: {
            title: BannerCriteriaSetTitles[BannerCriteriaKey.body],
            key: BannerCriteriaKey.body,
            type: CriteriaType.text,
            validate: validateTextValue
        },

        recommendations: {
            title: BannerCriteriaSetTitles[BannerCriteriaKey.recommendations],
            key: CampaignCriteriaKey.recommendations,
            type: CriteriaType.enumSet,
            enumSet: getEnumSetFrom(
                GdiRecommendationType,
                RecommendationEnumSetTitles
            ).filter(item =>
                isRecommendationTypeAvailable(
                    item.key as GdiRecommendationType,
                    banners,
                    options
                )
            ),
            validate: (value: FilterExpressionValue): boolean => {
                return (value.value as EnumSetItem[]).every(item =>
                    isRecommendationTypeAvailable(
                        item.key as GdiRecommendationType,
                        banners,
                        options
                    )
                );
            }
        },

        hasImage: {
            title: BannerCriteriaSetTitles[BannerCriteriaKey.hasImage],
            key: BannerCriteriaKey.hasImage,
            type: CriteriaType.boolean,
            validate: validateBooleanValue
        },

        hasTurbolanding: {
            title: BannerCriteriaSetTitles[BannerCriteriaKey.hasTurbolanding],
            key: BannerCriteriaKey.hasTurbolanding,
            type: CriteriaType.boolean,
            validate: validateBooleanValue
        },

        hasSitelinks: {
            title: BannerCriteriaSetTitles[BannerCriteriaKey.hasSitelinks],
            key: BannerCriteriaKey.hasSitelinks,
            type: CriteriaType.boolean,
            validate: validateBooleanValue
        },

        primaryStatus: {
            title: BannerCriteriaSetTitles[BannerCriteriaKey.primaryStatus],
            key: BannerCriteriaKey.primaryStatus,
            type: CriteriaType.enumSet,
            enumSet: getEnumSetFrom(
                BannerStatusForFilter,
                BannerStatusEnumSetTitles
            ),
            validate: validateEnumValue(BannerStatusForFilter)
        },

        cost: {
            title: BannerCriteriaSetTitles[BannerCriteriaKey.cost],
            key: BannerCriteriaKey.cost,
            type: CriteriaType.positiveNumber,
            validate: validatePositiveDoubleNumberValue
        },
        revenue: {
            title: BannerCriteriaSetTitles[BannerCriteriaKey.revenue],
            key: BannerCriteriaKey.revenue,
            type: CriteriaType.positiveNumber,
            validate: validatePositiveDoubleNumberValue
        },
        profitability: {
            title: BannerCriteriaSetTitles[BannerCriteriaKey.profitability],
            key: BannerCriteriaKey.profitability,
            type: CriteriaType.positiveNumber,
            validate: validatePositiveDoubleNumberValue
        },
        avgGoalCost: {
            title: BannerCriteriaSetTitles[BannerCriteriaKey.avgGoalCost],
            key: BannerCriteriaKey.avgGoalCost,
            type: CriteriaType.positiveNumber,
            validate: validatePositiveDoubleNumberValue,
            enumSetFields: enumSetFieldsForGoals
        },
        avgCpmCost: {
            title: BannerCriteriaSetTitles[BannerCriteriaKey.avgCpmCost],
            key: BannerCriteriaKey.avgCpmCost,
            type: CriteriaType.positiveNumber,
            validate: validatePositiveDoubleNumberValue
        },
        avgClickCost: {
            title: BannerCriteriaSetTitles[BannerCriteriaKey.avgClickCost],
            key: BannerCriteriaKey.avgClickCost,
            type: CriteriaType.positiveNumber,
            validate: validatePositiveDoubleNumberValue
        },
        shows: {
            title: BannerCriteriaSetTitles[BannerCriteriaKey.shows],
            key: BannerCriteriaKey.shows,
            type: CriteriaType.positiveNumber,
            validate: validatePositiveIntegerNumberValue
        },
        clicks: {
            title: BannerCriteriaSetTitles[BannerCriteriaKey.clicks],
            key: BannerCriteriaKey.clicks,
            type: CriteriaType.positiveNumber,
            validate: validatePositiveIntegerNumberValue
        },
        ctr: {
            title: BannerCriteriaSetTitles[BannerCriteriaKey.ctr],
            key: BannerCriteriaKey.ctr,
            type: CriteriaType.positiveNumber,
            validate: validatePositiveDoubleNumberValue
        },
        goals: {
            title: BannerCriteriaSetTitles[BannerCriteriaKey.goals],
            key: BannerCriteriaKey.goals,
            type: CriteriaType.positiveNumber,
            validate: validatePositiveIntegerNumberValue,
            enumSetFields: enumSetFieldsForGoals
        },
        conversionRate: {
            title: BannerCriteriaSetTitles[BannerCriteriaKey.conversionRate],
            key: BannerCriteriaKey.conversionRate,
            type: CriteriaType.positiveNumber,
            validate: validatePositiveDoubleNumberValue,
            enumSetFields: enumSetFieldsForGoals
        },
        bounceRate: {
            title: BannerCriteriaSetTitles[BannerCriteriaKey.bounceRate],
            key: BannerCriteriaKey.bounceRate,
            type: CriteriaType.positiveNumber,
            validate: validatePositiveDoubleNumberValue
        }
    };
};

// compact для того, чтобы не показывать в фильтрах некоторые статы. Если нет фичи или условий - не попадает в массив
const getBannerCriteriaContainers = (
    options: ClientInfo,
    isCpmCampaignsEnabled?: boolean,
    goals?: Record<string, Goal>,
    isGoalsStatEnabled?: boolean
): CriteriaContainer[] => {
    const criteria = getBannerCriteria(
        options,
        true,
        goals,
        isGoalsStatEnabled
    );

    return [
        {
            title: i18n('settings'),
            key: 'settings',
            criteria: [
                BannerCriteriaKey.id,
                BannerCriteriaKey.primaryStatus,
                BannerCriteriaKey.types,
                BannerCriteriaKey.title,
                BannerCriteriaKey.titleExtension,
                BannerCriteriaKey.body,
                BannerCriteriaKey.recommendations,
                BannerCriteriaKey.hasImage,
                BannerCriteriaKey.hasSitelinks,
                BannerCriteriaKey.hasTurbolanding
            ]
        },
        {
            title: i18n('metrics'),
            key: 'metrics',
            criteria: compact([
                BannerCriteriaKey.cost,
                BannerCriteriaKey.revenue,
                BannerCriteriaKey.profitability,
                BannerCriteriaKey.avgGoalCost,
                !isCpmCampaignsEnabled && BannerCriteriaKey.avgClickCost,
                BannerCriteriaKey.shows,
                isCpmCampaignsEnabled && BannerCriteriaKey.avgCpmCost,
                BannerCriteriaKey.clicks,
                BannerCriteriaKey.ctr,
                BannerCriteriaKey.goals,
                BannerCriteriaKey.conversionRate,
                BannerCriteriaKey.bounceRate
            ])
        }
    ].map(container => ({
        ...container,
        criteria: container.criteria.map(key => criteria[key])
    }));
};

export const getBannerFilterCriteriaContainers = (
    options: ClientInfo = defaultClientInfo,
    isCpmCampaignsEnabled?: boolean,
    goals?: Record<string, Goal>,
    isGoalsStatEnabled?: boolean
): CriteriaContainer[] => {
    return getBannerCriteriaContainers(
        options,
        isCpmCampaignsEnabled,
        goals,
        isGoalsStatEnabled
    ).map(container => {
        return {
            ...container,
            criteria: container.criteria.filter(criteria =>
                isCriteriaAvailable(criteria, options)
            )
        };
    });
};

export const getBannerFilterCriteria = (options: ClientInfo): Criteria[] => {
    const bannerCriteria = getBannerCriteria(options, false);

    return Object.keys(bannerCriteria).map(key => bannerCriteria[key]);
};

export const getAvailableBannerFilterCriteria = (
    options: ClientInfo = defaultClientInfo
): Criteria[] => {
    return getBannerFilterCriteria(options).filter(criteria =>
        isCriteriaAvailable(criteria, options)
    );
};

export const getAvailableFilterCriteria = (
    view: GridViewType,
    isBidCorrectionSearchEnabled?: boolean,
    hasOnlyCpmCampaigns?: boolean,
    clientInfo?: ClientInfo
): Criteria[] => {
    switch (view) {
        case 'campaigns':
            return getAvailableCampaignFilterCriteria(
                clientInfo || defaultClientInfo,
                isBidCorrectionSearchEnabled
            );
        case 'groups':
            return getAvailableGroupFilterCriteria(defaultClientInfo);
        case 'banners':
            return getAvailableBannerFilterCriteria(defaultClientInfo);
        case 'phrases':
            return getAvailablePhraseFilterCriteria(
                defaultClientInfo,
                hasOnlyCpmCampaigns
            );
        case 'retargetings':
            return getAvailableTargetingFilterCriteria(defaultClientInfo);
        case 'usersProfile':
            return getAvailableUsersProfileFilterCriteria(defaultClientInfo);
        case 'feedFilters':
            return getAvailableFeedFilterFilterCriteria(defaultClientInfo);
        default:
            return [];
    }
};

const DealTypeEnumSetTitles: { [key in DealType]: string } = {
    [DealType.PREFERRED_DEAL]: getDealTypeTitleByType(DealType.PREFERRED_DEAL),
    [DealType.PRIVATE_MARKETPLACE]: getDealTypeTitleByType(
        DealType.PRIVATE_MARKETPLACE
    )
};

const DealStatusEnumSetTitles: { [key in dealStatusForFilter]: string } = {
    [dealStatusForFilter.RECEIVED]: getDealStatusByStatusType(
        dealStatusForFilter.RECEIVED
    ),
    [dealStatusForFilter.ACTIVE]: getDealStatusByStatusType(
        dealStatusForFilter.ACTIVE
    ),
    [dealStatusForFilter.COMPLETED_BY_DATE]: getDealStatusByStatusType(
        dealStatusForFilter.COMPLETED_BY_DATE
    ),
    [dealStatusForFilter.COMPLETED_BY_PUBLISHER]: getDealStatusByStatusType(
        dealStatusForFilter.COMPLETED_BY_PUBLISHER
    ),
    [dealStatusForFilter.COMPLETED_BY_CLIENT]: getDealStatusByStatusType(
        dealStatusForFilter.COMPLETED_BY_CLIENT
    ),
    [dealStatusForFilter.ARCHIVED]: getDealStatusByStatusType(
        dealStatusForFilter.ARCHIVED
    )
};

export enum DealsCriteriaKey {
    id = 'id',
    clicks = 'clicks',
    cpc = 'cpc',
    name = 'name',
    cpm = 'cpm',
    shows = 'shows',
    minPrice = 'minPrice',
    spent = 'spent',
    status = 'status',
    ctr = 'ctr',
    type = 'type',
    numberOfLinkedCampaigns = 'numberOfLinkedCampaigns'
}

const dealCriteria: { [key in DealsCriteriaKey]: Criteria } = {
    id: {
        title: i18n('deal-id'),
        key: DealsCriteriaKey.id,
        type: CriteriaType.positiveNumberSet,
        validate: validatePositiveNumberSetValue
    },
    type: {
        title: i18n('deal-type'),
        key: DealsCriteriaKey.type,
        type: CriteriaType.enumSet,
        enumSet: getEnumSetFrom(DealType, DealTypeEnumSetTitles),
        validate: validateEnumValue(DealType)
    },
    name: {
        title: i18n('name'),
        key: DealsCriteriaKey.name,
        type: CriteriaType.text,
        validate: validateTextValue
    },
    status: {
        title: i18n('status'),
        key: DealsCriteriaKey.status,
        type: CriteriaType.enumSet,
        enumSet: getEnumSetFrom(
            dealStatusForFilter,
            DealStatusEnumSetTitles
        ).filter(el => el.key !== dealStatusForFilter.ARCHIVED),
        validate: validateEnumValue(dealStatusForFilter)
    },
    shows: {
        title: i18n('shows'),
        key: DealsCriteriaKey.shows,
        type: CriteriaType.positiveNumber,
        validate: validatePositiveIntegerNumberValue
    },
    clicks: {
        title: i18n('clicks'),
        key: DealsCriteriaKey.clicks,
        type: CriteriaType.positiveNumber,
        validate: validatePositiveIntegerNumberValue
    },
    cpc: {
        title: i18n('cpc'),
        key: DealsCriteriaKey.cpc,
        type: CriteriaType.positiveNumber,
        validate: validatePositiveDoubleNumberValue
    },
    cpm: {
        title: i18n('cpm'),
        key: DealsCriteriaKey.cpm,
        type: CriteriaType.positiveNumber,
        validate: validatePositiveDoubleNumberValue
    },
    ctr: {
        title: i18n('ctr'),
        key: DealsCriteriaKey.ctr,
        type: CriteriaType.positiveNumber,
        validate: validatePositiveDoubleNumberValue
    },
    minPrice: {
        title: i18n('min-price'),
        key: DealsCriteriaKey.minPrice,
        type: CriteriaType.positiveNumber,
        validate: validatePositiveDoubleNumberValue
    },
    spent: {
        title: i18n('spent'),
        key: DealsCriteriaKey.spent,
        type: CriteriaType.positiveNumber,
        validate: validatePositiveDoubleNumberValue
    },
    numberOfLinkedCampaigns: {
        title: i18n('numberOfLinkedCampaigns'),
        key: DealsCriteriaKey.numberOfLinkedCampaigns,
        type: CriteriaType.positiveNumber,
        validate: validatePositiveIntegerNumberValue
    }
};

const dealCriteriaContainers: CriteriaContainer[] = [
    {
        title: i18n('settings'),
        key: 'settings',
        criteria: [
            DealsCriteriaKey.id,
            DealsCriteriaKey.type,
            DealsCriteriaKey.name,
            DealsCriteriaKey.status,
            DealsCriteriaKey.numberOfLinkedCampaigns
        ]
    },
    {
        title: i18n('metrics'),
        key: 'metrics',
        criteria: [
            DealsCriteriaKey.spent,
            DealsCriteriaKey.shows,
            DealsCriteriaKey.minPrice,
            DealsCriteriaKey.cpm,
            DealsCriteriaKey.clicks,
            DealsCriteriaKey.cpc,
            DealsCriteriaKey.ctr
        ]
    }
].map(container => ({
    ...container,
    criteria: container.criteria.map(key => dealCriteria[key])
}));

export const getDealFilterCriteria = (): Criteria[] => {
    return Object.keys(dealCriteria).map(key => dealCriteria[key]);
};

export const getDealFilterCriteriaContainers = (): CriteriaContainer[] => {
    return dealCriteriaContainers.map(container => {
        return { ...container };
    });
};

export const getEnumSetFieldsForGoals = (
    goals?: Record<string, Goal>
): EnumSetItem[] | undefined => {
    if (!goals) {
        return undefined;
    }

    return [
        {
            title: i18n('statistics-by-all-goals'),
            key: StatisticsByAllGoals
        }
    ].concat(
        Object.keys(goals).map(goalId => {
            const currentGoal = goals[goalId];

            return {
                title: currentGoal.name,
                key: currentGoal.id
            };
        })
    );
};
